// src/pages/NewsDetailPage.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import PageBanner from "../components/common/PageBanner";
import { useLocale } from "../contexts/LocaleContext";
import { getNewsPageData, getNewsletterData } from "../api/apiService";
import SpecialNewsLayout from "../components/news/SpecialNewsLayout";
import NewsBackButton from "../components/news/NewsBackButton";
import NewsMeta from "../components/news/NewsMeta";
import NewsContentSection from "../components/news/NewsContentSection";
import NewsImageZoomModal from "../components/news/NewsImageZoomModal";
import NewsPrevNextNav from "../components/news/NewsPrevNextNav";
import { pickUrlStringFromNewsItem } from "../utils/newsLink";

const NewsDetailPage = () => {
  const { id } = useParams();
  const { locale } = useLocale();

  const [newsPageData, setNewsPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newsletterEntry, setNewsletterEntry] = useState(null);
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterError, setNewsletterError] = useState(null);
  // zoomImage: media object | null
  const [zoomImage, setZoomImage] = useState(null);

  useEffect(() => {
    const fetchNewsContent = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getNewsPageData(locale);
        setNewsPageData(data);
      } catch (err) {
        setError(err.message || "Failed to load content.");
        setNewsPageData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchNewsContent();
  }, [locale]);

  const banner = newsPageData?.banner;
  const bannerTitle = banner?.title || "News";
  const bannerImageUrl = banner?.image?.[0]?.url;

  // cas_news can be either an object with .list or an array with [0].list
  const rawNewsList =
    newsPageData?.cas_news?.list || newsPageData?.cas_news?.[0]?.list || [];

  const selectedNews = useMemo(() => {
    if (!newsPageData || !rawNewsList || rawNewsList.length === 0) {
      return null;
    }
    const targetId = isNaN(Number(id)) ? id : Number(id);

    const item = rawNewsList.find((newsItem) => {
      // Match either numeric id or documentId/string id
      if (newsItem.id === targetId || newsItem.documentId === targetId) {
        return true;
      }
      if (
        String(newsItem.id) === String(id) ||
        String(newsItem.documentId) === String(id)
      ) {
        return true;
      }
      return false;
    });

    if (!item) return null;

    const date = item.newsDate || null;
    const title = item.newsTitle || "";
    const product = item.newsProduct || "";
    // Rich content from API
    const content = item.newsContent || null;
    const subtitle = item.newsSubtitle || "";
    const media = item.media || [];
    const youtube_id = item.youtube_id || "";
    const url = pickUrlStringFromNewsItem(item);
    const newsletter = item.newsletter;

    return {
      ...item,
      date,
      title,
      product,
      content,
      subtitle,
      media,
      youtube_id,
      url,
      newsletter,
    };
  }, [newsPageData, rawNewsList, id]);

  // Ordered newest first: Previous = older (higher index), Next = newer (lower index)
  const { previousTarget, nextTarget } = useMemo(() => {
    if (!rawNewsList || rawNewsList.length === 0 || !selectedNews) {
      return { previousTarget: null, nextTarget: null };
    }

    const ordered = [...rawNewsList].sort((a, b) => {
      try {
        const dateA = a.newsDate ? new Date(a.newsDate) : new Date(0);
        const dateB = b.newsDate ? new Date(b.newsDate) : new Date(0);
        if (isNaN(dateA.getTime())) return 1;
        if (isNaN(dateB.getTime())) return -1;
        return dateB - dateA; // newest first
      } catch (e) {
        return 0;
      }
    });

    const currentIndex = ordered.findIndex((item) => {
      return (
        String(item.id) === String(selectedNews.id) ||
        (item.documentId && String(item.documentId) === String(selectedNews.documentId))
      );
    });

    if (currentIndex === -1) {
      return { previousTarget: null, nextTarget: null };
    }

    const newerItem = currentIndex > 0 ? ordered[currentIndex - 1] : null;
    const olderItem =
      currentIndex < ordered.length - 1 ? ordered[currentIndex + 1] : null;

    const toTarget = (item) => {
      if (!item) return null;
      const id = item.documentId ?? item.id ?? null;
      const url = pickUrlStringFromNewsItem(item);
      if (!url && (id == null || id === "")) return null;
      return { id, url };
    };

    return {
      previousTarget: toTarget(olderItem),
      nextTarget: toTarget(newerItem),
    };
  }, [rawNewsList, selectedNews]);

  // Load newsletter detail from dedicated API when this news has a newsletter relation
  useEffect(() => {
    const loadNewsletter = async () => {
      if (!selectedNews || !selectedNews.newsletter) {
        setNewsletterEntry(null);
        setNewsletterError(null);
        setNewsletterLoading(false);
        return;
      }

      const relatedNewsletter = selectedNews.newsletter;
      const targetId = relatedNewsletter.documentId || relatedNewsletter.id;
      if (!targetId) {
        setNewsletterEntry(null);
        setNewsletterError(null);
        setNewsletterLoading(false);
        return;
      }

      setNewsletterLoading(true);
      setNewsletterError(null);
      try {
        const data = await getNewsletterData(locale);

        // Strapi collection: data can be array or { data: [...] }
        const rawItems = Array.isArray(data) ? data : data?.data || [];

        const found = rawItems.find((item) => {
          const base = item.attributes || item;
          const idMatch =
            item.id === relatedNewsletter.id ||
            String(item.id) === String(relatedNewsletter.id);
          const docIdMatch =
            base.documentId &&
            (base.documentId === relatedNewsletter.documentId ||
              String(base.documentId) === String(relatedNewsletter.documentId));
          const targetMatch =
            targetId &&
            (item.id === targetId ||
              base.documentId === targetId ||
              String(item.id) === String(targetId) ||
              String(base.documentId) === String(targetId));
          return idMatch || docIdMatch || targetMatch;
        });

        if (!found) {
          setNewsletterEntry(null);
        } else {
          const normalized = found.attributes
            ? { id: found.id, ...found.attributes }
            : found;
          setNewsletterEntry(normalized);
        }
      } catch (err) {
        setNewsletterError(err.message || "Failed to load newsletter content.");
        setNewsletterEntry(null);
      } finally {
        setNewsletterLoading(false);
      }
    };

    loadNewsletter();
  }, [selectedNews, locale]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[calc(100vh-200px)]">
        <p className="text-xl text-gray-500">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4 text-center">
        <p className="text-red-500 text-xl">Error: {error}</p>
      </div>
    );
  }

  if (!newsPageData || !selectedNews) {
    return (
      <div className="container mx-auto p-4 text-center">
        <p className="text-gray-700 text-xl">News content not available.</p>
      </div>
    );
  }

  const hasContent = !!selectedNews.content && Array.isArray(selectedNews.content) && selectedNews.content.length > 0;
  const hasYoutube = !!selectedNews.youtube_id;
  const hasMedia = Array.isArray(selectedNews.media) && selectedNews.media.length > 0;

  const isPdf = selectedNews.media[0]?.formats === null || selectedNews.media[0]?.formats === undefined;
  const isImage = !isPdf && selectedNews.media?.formats !== null;

  // Use special layout whenever this news has a related newsletter AND newsletter data loaded
  const hasRelatedNewsletter = !!selectedNews.newsletter;
  const isSpecialLayout = hasRelatedNewsletter && !!newsletterEntry;


  return (
    <div className="news-detail-page min-h-screen">
      <PageBanner
        title={bannerTitle}
        titleColorIsWhite={banner?.titleColorIsWhite ?? true}
        imageUrl={bannerImageUrl}
        bannerHeightClass="h-48 sm:h-56 md:h-64 lg:h-72"
      />

      <section className="section-surface-mist w-full py-10 md:py-14 lg:py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back button */}
        <div className="mb-6">
          <NewsBackButton />
        </div>

        {isSpecialLayout ? (
          <SpecialNewsLayout newsletter={newsletterEntry} />
        ) : (
          <div className="panel-elevated rounded-2xl p-6 sm:p-8 lg:p-10">
            {/* Title */}
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4 leading-tight">
              {selectedNews.title}
            </h1>

            {/* Meta: date + product */}
            <NewsMeta date={selectedNews.date} product={selectedNews.product} />

            {/* Content section */}
            <NewsContentSection
              news={selectedNews}
              onImageClick={(mediaItem) => setZoomImage(mediaItem)}
            />
          </div>
        )}

        {/* Prev / Next navigation - used for both normal and special layouts */}
        <NewsPrevNextNav
          previousTarget={previousTarget}
          nextTarget={nextTarget}
        />
        </div>
      </section>

      {/* Image Zoom Modal */}
      <NewsImageZoomModal
        media={zoomImage}
        onClose={() => setZoomImage(null)}
        titleFallback={selectedNews.title}
      />
    </div>
  );
};

export default NewsDetailPage;


